/**
 * Assignment step editor v2 — the row model (Ram, 2026-10-22).
 *
 * A row is one entry of `node.assignments`, read as a sentence:
 *     total  [becomes]  count × price
 * name first, then the verb, then the value. The model only READS an
 * assignment; it never rewrites one that was not edited. `rowsToAssignments`
 * hands back the very objects it was given, so loading a step into v2 and
 * saving it without an edit leaves the node identical — and the generated code
 * with it.
 *
 * New and edited values are written the way 97.5% of rows already are:
 * `valueSource: 'expression'`, `expression` = the keyboard's C, `expressionAst:
 * null`. A row with a Direct or Variable value (the old editor's other two
 * sources) is shown read-only as the C its value generates, and is converted
 * only when its value is edited — and then from that same C text, so a char
 * array's `Ravi` (which codegen quotes) becomes the expression `"Ravi"`.
 */
import { needsStringQuotes, applyArrayIndices } from '@/utils/codeGenerator';

// ---- verbs -------------------------------------------------------------------
// The operator a row applies, in words. `key` is a test-id-safe name.
export const VERBS = [
  { op: '=', key: 'set', words: 'becomes' },
  { op: '+=', key: 'add', words: 'goes up by' },
  { op: '-=', key: 'sub', words: 'goes down by' },
  { op: '*=', key: 'mul', words: 'is multiplied by' },
  { op: '/=', key: 'div', words: 'is divided by' },
  { op: '%=', key: 'mod', words: 'keeps the remainder after dividing by' },
  { op: '++', key: 'inc', words: 'goes up by 1' },
  { op: '--', key: 'dec', words: 'goes down by 1' },
];
// Bitwise compound operators: shown as their C symbol, and never in words-only mode.
export const BITWISE_VERBS = [
  { op: '&=', key: 'and', words: '&=', symbol: true },
  { op: '|=', key: 'or', words: '|=', symbol: true },
  { op: '^=', key: 'xor', words: '^=', symbol: true },
  { op: '<<=', key: 'shl', words: '<<=', symbol: true },
  { op: '>>=', key: 'shr', words: '>>=', symbol: true },
];
export const NO_VALUE_OPS = ['++', '--'];

const ALL_VERBS = [...VERBS, ...BITWISE_VERBS];
export const verbFor = (op) => ALL_VERBS.find((v) => v.op === (op || '=')) || { op, key: 'other', words: op, symbol: true };
export const verbWords = (op) => verbFor(op).words;
export const isNoValueOp = (op) => NO_VALUE_OPS.includes(op);

/** The verbs the dropdown offers; the row's current one is never hidden. */
export function verbChoices({ wordsOnly = false, current = '=' } = {}) {
  const out = wordsOnly ? [...VERBS] : [...ALL_VERBS];
  if (!out.some((v) => v.op === (current || '='))) out.push(verbFor(current));
  return out;
}

// ---- resources -----------------------------------------------------------------
const rtOf = (r) => String((r && (r.resourceType || r.type)) || '').toLowerCase();
export const findTargetResource = (a, resources = []) => (a && a.targetVariable
  ? (resources || []).find((r) => r && r.id === a.targetVariable) || null
  : null);

/** A char array: a piece of text (assigned whole, with strcpy). */
export const isTextResource = (r) => {
  if (!r) return false;
  const rt = rtOf(r);
  const et = String(r.elementType || r.dataType || '').trim();
  return (rt === 'array_1d' || rt === 'array') && /^char\b/.test(et);
};

// ---- the target -------------------------------------------------------------------
/**
 * The target as the program writes it: `total`, `s.total`, `marks[i]`,
 * `grid[r][c]`, `db[i].gpa`. A position not chosen yet shows as `?`; a text
 * (char array) with no position is the whole text.
 */
export function targetText(a, resources = []) {
  if (!a) return '';
  if (a.unpack) {
    return (a.targetVariables || [])
      .map((t) => (t && (t.variableName || ((resources || []).find((r) => r.id === t.variableId) || {}).name)) || '?')
      .join(', ');
  }
  const res = findTargetResource(a, resources);
  const path = a.fullPath || (res && res.name) || '';
  if (!path) return '';
  const idx = String(a.arrayIndex ?? '').trim();
  const idx2 = String(a.arrayIndex2 ?? '').trim();
  const hasSlot = /\[\s*\]/.test(path);
  if (!hasSlot && a.accessType !== 'array') return path;
  const grid = a.accessType === 'array' && rtOf(res) === 'array_2d';
  if (!idx) {
    // The whole array: a text is assigned whole; anything else needs a position.
    if (isTextResource(res) && /\[\s*\]\s*$/.test(path) && !/\[\s*\]\s*[.[]/.test(path)) return path.replace(/\[\s*\]\s*$/, '');
    if (a.accessType === 'array' && !/\[\s*\]\s*[.[]/.test(path)) {
      const base = path.replace(/\[\s*\]\s*$/, '');
      return grid ? `${base}[?][?]` : `${base}[?]`;
    }
    return path.replace(/\[\s*\]/g, '[?]');
  }
  const out = applyArrayIndices(path, a.accessType === 'array', idx, idx2 || undefined);
  return grid && !idx2 ? `${out}[?]` : out;
}

/** Which position is still blank, as a message (or null). */
export function blankPositionMessage(a, resources = []) {
  if (!a || a.unpack || !a.targetVariable) return null;
  const t = targetText(a, resources);
  if (!t.includes('[?]')) return null;
  const res = findTargetResource(a, resources);
  const name = (res && res.name) || String(a.fullPath || '').split(/[[.]/)[0];
  return `Choose which position of ${name} to set.`;
}

// ---- the value -----------------------------------------------------------------------
/** What kind of value the row holds. */
export function valueKind(a) {
  if (!a) return 'none';
  if (a.unpack) return 'unpack';
  if (isNoValueOp(a.operator)) return 'none';
  const src = a.valueSource || 'direct';
  if (src === 'expression') return 'expression';
  if (src === 'variable') return 'variable';
  if (src === 'comprehension') return 'comprehension';
  if (src === 'lambda') return 'lambda';
  return 'direct';
}

/**
 * Would codegen quote this Direct value? The same test codeGenerator.js makes
 * (a `string` / `char[N]` target and a value that is not already a literal,
 * a number or a reference).
 */
function codegenQuotesDirect(a, resources) {
  const res = findTargetResource(a, resources);
  const t = String(a.targetResolvedType || (res && res.dataType) || '').toLowerCase().trim();
  const charArrayType = t === 'string' || (/\bchar\b/.test(t) && /\[/.test(t));
  return charArrayType && needsStringQuotes(String(a.directValue), resources);
}

/** Codegen's own char-array test for the target (it strcpy's into one). */
function targetIsCharArray(a, resources) {
  const recorded = String(a.targetResolvedType || '').trim();
  if (/^char\s*\[/.test(recorded)) return true;
  if (recorded && !/^char\b/.test(recorded)) return false;
  const r = (resources || []).find((x) => x.id === a.targetVariable);
  if (!r) return false;
  const et = String(r.elementType || r.dataType || '').trim();
  return (r.resourceType === 'array_1d' || r.resourceType === 'ARRAY_1D') && (et === 'char' || /^char\b/.test(et));
}

/**
 * The value as C text — what the row shows, and what the keyboard opens with
 * when the value is edited. An Expression row is its expression. A Direct row
 * is the value codegen emits (quoted when codegen quotes it; a bare word into a
 * text is shown as the text it was meant to be). A Variable row is the path
 * codegen emits (`marks[i]`, `p->next`, `&x`).
 */
export function valueText(a, resources = []) {
  const kind = valueKind(a);
  if (kind === 'expression') return String(a.expression || '');
  if (kind === 'direct') {
    const v = a.directValue;
    if (v === undefined || v === null || v === '') return '';
    const s = String(v);
    if (codegenQuotesDirect(a, resources)) return `"${s}"`;
    // Codegen strcpy's a bare word into a char array without quoting it (the
    // target's recorded type was never captured). It was meant as text.
    if (targetIsCharArray(a, resources) && needsStringQuotes(s, resources)) return `"${s}"`;
    return s;
  }
  if (kind === 'variable') {
    if (!a.sourceVariable) return '';
    const src = (resources || []).find((r) => r.id === a.sourceVariable);
    const base = a.sourceFullPath || (src && src.name) || a.sourceVariable;
    return applyArrayIndices(
      base,
      a.sourceAccessType === 'array',
      a.sourceArrayIndex != null ? a.sourceArrayIndex : undefined,
      a.sourceArrayIndex2 != null ? a.sourceArrayIndex2 : undefined,
    );
  }
  if (kind === 'comprehension') {
    const c = a.comprehension || {};
    if (!c.outExpr || !c.varName || !c.iterable) return '';
    return `[${c.outExpr} for ${c.varName} in ${c.iterable}${c.filter ? ` if ${c.filter}` : ''}]`;
  }
  if (kind === 'lambda') {
    const l = a.lambda || {};
    if (!l.body) return '';
    return l.params ? `lambda ${l.params}: ${l.body}` : `lambda: ${l.body}`;
  }
  if (kind === 'unpack') {
    if (a.valueSource === 'variable') {
      const src = (resources || []).find((r) => r.id === a.sourceVariable);
      return String(a.sourceFullPath || (src && src.name) || '').replace(/\[\s*\]\s*$/, '');
    }
    return String(a.expression || '');
  }
  return '';
}

/** Does the row have the value it needs? (`++` / `--` need none.) */
export function hasValue(a) {
  const kind = valueKind(a);
  if (kind === 'none') return true;
  if (kind === 'expression') return !!String(a.expression || '').trim();
  if (kind === 'direct') return !!a.directValue || a.directValue === 0;
  if (kind === 'variable') return !!a.sourceVariable;
  return !!valueText(a, []).trim();
}

/**
 * One row, described. `editable` is false for the Python-only shapes
 * (comprehension, lambda, unpacking) the C editor does not write: they are
 * shown read-only and can only be deleted.
 */
export function describeAssignment(a, resources = []) {
  const kind = valueKind(a);
  return {
    assignment: a,
    target: targetText(a, resources),
    op: (a && a.operator) || '=',
    verb: verbFor(a && a.operator),
    noValue: isNoValueOp(a && a.operator),
    kind,
    value: valueText(a, resources),
    readOnlyValue: kind !== 'expression' && kind !== 'none',
    editable: !['comprehension', 'lambda', 'unpack'].includes(kind),
  };
}

export function describeAssignments(node, resources = []) {
  const list = Array.isArray(node && node.assignments) ? node.assignments : [];
  return list.map((a) => describeAssignment(a, resources));
}

/** The rows back to `assignments`: the same objects, in row order. */
export const rowsToAssignments = (rows) => rows.map((r) => r.assignment);

// ---- writing ------------------------------------------------------------------------
export const generateAssignmentId = () => `assign-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

/** A blank row, in the old editor's shape. */
export const blankAssignment = () => ({
  id: generateAssignmentId(),
  targetVariable: '',
  accessType: 'scalar',
  accessPath: '',
  memberName: '',
  arrayIndex: '',
  fullPath: '',
  operator: '=',
  valueSource: 'direct',
  directValue: '',
  sourceVariable: '',
  sourceAccessType: 'scalar',
  sourceAccessPath: '',
  sourceMemberName: '',
  sourceArrayIndex: '',
  sourceFullPath: '',
  expression: '',
});

/** Target fields for a picked target (the shape VariablePicker hands the old row). */
export function targetFields(pick, indices = []) {
  const out = {
    targetVariable: pick.variableId,
    accessType: pick.accessType,
    accessPath: pick.accessPath || '',
    memberName: pick.memberName || '',
    fullPath: pick.fullPath,
    targetResolvedType: pick.dataType || null,
    arrayIndex: String(indices[0] ?? '').trim(),
  };
  if (pick.dims === 2) out.arrayIndex2 = String(indices[1] ?? '').trim();
  return out;
}

/** The row with its value written as an expression (the keyboard's C). */
export const withExpression = (a, expression) => ({
  ...a, valueSource: 'expression', expression, expressionAst: null,
});

// ---- the targets a step can set ----------------------------------------------------------
const MAX_DEPTH = 3;

/**
 * Everything a row can set, as the old target picker offers it: plain values,
 * every field of a record (nested ones too), a list's or grid's positions, a
 * list of records' fields, and a text whole. Each candidate carries the pick
 * the old picker would have produced, so a row written here is a row the old
 * editor could have written.
 *   { label, key, dims, root, resource, pick }
 * `label` shows an open position as `[ ]`; `key` is the same with `[]`.
 */
export function targetCandidates(variables = [], allResources = [], { getStructMembers, structArrayElem, findStructDefinition } = {}) {
  const out = [];
  const add = (c) => { if (!out.some((o) => o.key === c.key)) out.push(c); };
  const labelOf = (key) => key.replace(/\[\]/g, '[ ]');
  const membersOf = (r) => {
    try { return (getStructMembers && getStructMembers(r, allResources)) || []; } catch (e) { return []; }
  };
  const members = (parent, resource, depth, dims) => {
    const list = membersOf(parent);
    list.forEach((m) => {
      if (m.isNestedStruct && depth < MAX_DEPTH) {
        members({ ...m, resourceType: 'struct_instance', memberType: 'struct' }, resource, depth + 1, dims);
        return;
      }
      add({
        key: m.fullPath,
        label: labelOf(m.fullPath),
        dims,
        root: resource.name,
        resource,
        pick: {
          variableId: resource.id, accessType: 'member', accessPath: m.accessPath || '',
          memberName: m.name, fullPath: m.fullPath, dataType: m.dataType || null, dims,
        },
      });
    });
  };
  // A whole record (`p1 = p2`, `pg_class[j] = pg_class[i]`): kind record, with its struct type.
  const wholeRecord = (r, tag, dims, accessType, fullPath) => {
    const key = dims ? `${r.name}[]${dims === 2 ? '[]' : ''}` : r.name;
    const structType = recordType(tag);
    add({
      key, label: labelOf(key), dims, root: r.name, resource: r, kind: 'record', structType,
      pick: { variableId: r.id, accessType, accessPath: '', memberName: '', fullPath, dataType: structType, dims },
    });
  };
  (variables || []).forEach((r) => {
    // A loop counter has no id: a row cannot name it (as in the old picker).
    if (!r || !r.name || !r.id) return;
    const rt = rtOf(r);
    const elem = structArrayElem ? structArrayElem(r) : null;
    if (elem && findStructDefinition && findStructDefinition(elem, allResources)) {
      const dims = rt === 'array_2d' ? 2 : 1;
      wholeRecord(r, elem, dims, 'array', `${r.name}[]`);
      const virtual = {
        ...r, fullPath: `${r.name}[]${dims === 2 ? '[]' : ''}`, structTypeRef: elem,
        resourceType: 'struct_instance', isArrayOfStruct: true,
      };
      members(virtual, r, 1, dims);
      return;
    }
    if (rt === 'struct_instance' || rt === 'union_instance') {
      const before = out.length;
      const list = membersOf(r);
      if (list.length) wholeRecord(r, r.structTypeRef || r.dataType, 0, 'scalar', r.name);
      members(r, r, 1, 0);
      if (out.length > before) return;
    }
    // A record passed in (`struct Options opts`): the whole of it, and its fields.
    if (rt === 'variable' && !/[*[]/.test(String(r.dataType || ''))) {
      const list = membersOf(r);
      if (list.length) {
        wholeRecord(r, r.dataType, 0, 'scalar', r.name);
        members(r, r, 1, 0);
        return;
      }
    }
    if (isTextResource(r)) {
      add({
        key: r.name, label: r.name, dims: 0, root: r.name, resource: r, text: true,
        pick: { variableId: r.id, accessType: 'array', accessPath: '', memberName: '', fullPath: `${r.name}[]`, dataType: null, dims: 0 },
      });
      // One letter of it: name[i] = 'R'.
      add({
        key: `${r.name}[]`, label: `${r.name}[ ]`, dims: 1, root: r.name, resource: r, kind: 'character', letter: true,
        pick: { variableId: r.id, accessType: 'array', accessPath: '', memberName: '', fullPath: `${r.name}[]`, dataType: 'char', dims: 1 },
      });
      return;
    }
    if (rt === 'array_1d' || rt === 'array' || rt === 'list' || rt === 'array_2d') {
      const dims = rt === 'array_2d' ? 2 : 1;
      const key = `${r.name}[]${dims === 2 ? '[]' : ''}`;
      add({
        key, label: labelOf(key), dims, root: r.name, resource: r,
        pick: { variableId: r.id, accessType: 'array', accessPath: '', memberName: '', fullPath: `${r.name}[]`, dataType: null, dims },
      });
      return;
    }
    const ptrType = pointerTypeOf(r);
    if (ptrType) {
      add({
        key: r.name, label: r.name, dims: 0, root: r.name, resource: r,
        pick: { variableId: r.id, accessType: 'scalar', accessPath: '', memberName: '', fullPath: r.name, dataType: ptrType, dims: 0 },
      });
      // A pointer to a record: its fields through the arrow (p->next = q).
      membersOf(r).filter((m) => String(m.accessPath || '').startsWith('->')).length && members(r, r, 1, 0);
      return;
    }
    add({
      key: r.name, label: r.name, dims: 0, root: r.name, resource: r,
      pick: { variableId: r.id, accessType: 'scalar', accessPath: '', memberName: '', fullPath: r.name, dataType: r.dataType || null, dims: 0 },
    });
  });
  return out;
}

/** `Student`, `struct_Student`, `struct Student` → `struct Student` (a typedef'd name stays itself). */
export function recordType(tag) {
  const t = String(tag || '').trim().replace(/^struct_/, 'struct ').replace(/\s+/g, ' ');
  if (!t) return null;
  if (/^(struct|union) /.test(t)) return t;
  return `struct ${t}`;
}

/**
 * The C pointer type a resource holds, or null: a `pointer` resource
 * (`int*`, `struct_Node*`), or any value whose declared type ends in `*`
 * (a parameter `int *a`, `struct Node **head`).
 */
export function pointerTypeOf(r) {
  if (!r) return null;
  const rt = rtOf(r);
  const raw = String(r.dataType || '').trim();
  if (rt === 'pointer') return /\*\s*$/.test(raw) ? raw : `${r.pointsToType || raw || 'int'}*`;
  if ((rt === 'variable' || rt === '') && /\*\s*$/.test(raw)) return raw;
  return null;
}

/** What a pointer resource points at (`int`, `struct Node*` for a `struct Node**`), or null. */
export function pointeeTypeOf(r) {
  const t = pointerTypeOf(r);
  if (!t) return null;
  if (rtOf(r) === 'pointer' && r.pointsToType && !/\*\s*$/.test(String(r.dataType || ''))) return String(r.pointsToType).trim();
  return t.replace(/\s*\*\s*$/, '').trim() || null;
}

/**
 * What a typed target means: `marks[i]` → the `marks[ ]` candidate at i;
 * `s.total` → that field; `db[k].gpa` → db's gpa at k. A bare name of a list
 * picks the list with its position still to choose.
 *   → { candidate, indices } | { record: resource } | null
 */
export function resolveTypedTarget(text, candidates = []) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const indices = [];
  const key = raw.replace(/\s+/g, '').replace(/\[([^\]]*)\]/g, (_m, inner) => { indices.push(inner); return '[]'; });
  const exact = candidates.find((c) => c.key === key);
  if (exact) return { candidate: exact, indices: exact.dims ? indices.slice(0, exact.dims) : [] };
  if (/^[A-Za-z_]\w*$/.test(key)) {
    const ofRoot = candidates.filter((c) => c.root === key);
    if (ofRoot.length === 1 && ofRoot[0].dims > 0 && ofRoot[0].key.startsWith(`${key}[]`) && !ofRoot[0].key.includes('.')) {
      return { candidate: ofRoot[0], indices: [] };
    }
    if (ofRoot.length > 0) return { record: ofRoot[0].resource, fields: ofRoot };
  }
  return null;
}

/** A target's label with its positions filled in (for the field and the row). */
export function fillPositions(label, indices = []) {
  let k = 0;
  return String(label || '').replace(/\[ \]/g, () => {
    const v = String(indices[k] ?? '').trim();
    k += 1;
    return `[${v || ' '}]`;
  });
}

// ---- sentences (Ram, 2026-10-22: verb first, the name always second) -----------------------
/**
 * How a row reads, verb first: "Set total to price × units", "Increase count
 * by 1", "Decrease stock by sold". A VIEW over the stored operator — nothing is
 * rewritten: an old `count++` reads "Increase count by 1" and stays `++`.
 * `*=`, `/=`, `%=` and the bitwise operators read as a plain sentence and are
 * shown read-only; a new row is never offered them.
 *   → { verb, joiner, value (C, '1' for ++ / --), key, offered }
 */
export const SENTENCES = {
  '=': { key: 'set', verb: 'Set', joiner: 'to', offered: true },
  '+=': { key: 'increase', verb: 'Increase', joiner: 'by', offered: true },
  '++': { key: 'increase', verb: 'Increase', joiner: 'by', offered: true, one: true },
  '-=': { key: 'decrease', verb: 'Decrease', joiner: 'by', offered: true },
  '--': { key: 'decrease', verb: 'Decrease', joiner: 'by', offered: true, one: true },
  '*=': { key: 'multiply', verb: 'Multiply', joiner: 'by', offered: false },
  '/=': { key: 'divide', verb: 'Divide', joiner: 'by', offered: false },
  '%=': { key: 'remainder', verb: 'Keep the remainder of', joiner: 'after dividing by', offered: false },
  '&=': { key: 'bitand', verb: 'Keep only the bits of', joiner: 'also in', offered: false },
  '|=': { key: 'bitor', verb: 'Add to the bits of', joiner: 'the bits of', offered: false },
  '^=': { key: 'bitxor', verb: 'Flip the bits of', joiner: 'that are set in', offered: false },
  '<<=': { key: 'shl', verb: 'Shift the bits of', joiner: 'left by', offered: false },
  '>>=': { key: 'shr', verb: 'Shift the bits of', joiner: 'right by', offered: false },
};

/** The verbs a row can be written with, in the form's order. */
export const FORM_VERBS = [
  { key: 'set', words: 'Set', op: '=', joiner: 'to' },
  { key: 'increase', words: 'Increase', op: '+=', joiner: 'by' },
  { key: 'decrease', words: 'Decrease', op: '-=', joiner: 'by' },
  { key: 'swap', words: 'Swap', op: null, joiner: 'and' },
];

export function sentenceFor(a, resources = []) {
  const op = (a && a.operator) || '=';
  const s = SENTENCES[op] || { key: 'other', verb: op, joiner: 'with', offered: false };
  return {
    ...s,
    op,
    target: targetText(a, resources),
    value: s.one ? '1' : valueText(a, resources),
  };
}

// ---- Swap: three ordinary rows, shown as one ------------------------------------------------
/**
 * "Swap a and b" is stored as three ordinary rows that codegen reads like any
 * other — `swap_tmp = a; a = b; b = swap_tmp;` — each carrying `swapGroup` (a
 * marker codegen never reads). The panel shows them as one row.
 */
export const isSwapRow = (a) => !!(a && a.swapGroup);

/** The first free name for the temporary: swap_tmp, swap_tmp2, … */
export function swapTempName(names = []) {
  const taken = new Set(names || []);
  if (!taken.has('swap_tmp')) return 'swap_tmp';
  for (let n = 2; ; n += 1) if (!taken.has(`swap_tmp${n}`)) return `swap_tmp${n}`;
}

/**
 * The step's rows as the panel lists them: single rows, and each complete
 * swap group (three rows in a row with the same swapGroup) as one item.
 *   → [{ type: 'row', indices: [i] } | { type: 'swap', indices: [i, i+1, i+2], a, b, tmp, group }]
 */
export function displayItems(assignments = [], resources = []) {
  const list = Array.isArray(assignments) ? assignments : [];
  const out = [];
  for (let i = 0; i < list.length; i += 1) {
    const g = list[i] && list[i].swapGroup;
    if (g && i + 2 < list.length && list[i + 1] && list[i + 1].swapGroup === g && list[i + 2] && list[i + 2].swapGroup === g) {
      out.push({
        type: 'swap',
        indices: [i, i + 1, i + 2],
        group: g,
        tmp: list[i].targetVariable,
        tmpName: targetText(list[i], resources),
        a: targetText(list[i + 1], resources),
        b: targetText(list[i + 2], resources),
      });
      i += 2;
    } else {
      out.push({ type: 'row', indices: [i] });
    }
  }
  return out;
}

/**
 * The three rows of "Swap a and b". a, b: { candidate, indices } (a target as
 * the form resolved it); tmp: the temporary's resource; group: the marker.
 */
export function buildSwapRows({ a, b, tmp, group }) {
  const text = (x) => fillPositions(x.candidate.label, x.indices || []);
  const tmpPick = { variableId: tmp.id, accessType: 'scalar', accessPath: '', memberName: '', fullPath: tmp.name, dataType: tmp.dataType || null };
  const row = (pick, indices, expression, k) => ({
    ...withExpression({ ...blankAssignment(), ...targetFields(pick, indices), operator: '=' }, expression),
    swapGroup: group,
    id: `${generateAssignmentId()}-${k}`,
  });
  return [
    row(tmpPick, [], text(a), 0),
    row(a.candidate.pick, a.indices || [], text(b), 1),
    row(b.candidate.pick, b.indices || [], tmp.name, 2),
  ];
}

// ---- the kind a target holds -------------------------------------------------------------------
/** The C type a candidate's target holds (`int` for marks[i] of an int list, the field's type, …). */
export function candidateType(c) {
  if (!c) return null;
  if (c.text) return 'char[]';
  const pick = c.pick || {};
  if (pick.dataType) return pick.dataType;
  const r = c.resource || {};
  if (c.dims) return r.elementType || r.dataType || null;
  return r.dataType || null;
}

/** Add `*p` targets (the value a pointer points at) to a candidate list: any pointer, parameters too. */
export function withPointerTargets(candidates = [], variables = []) {
  const out = [...candidates];
  (variables || []).forEach((r) => {
    if (!r || !r.id || !r.name) return;
    const pointee = pointeeTypeOf(r);
    if (!pointee || /^void$/.test(pointee)) return;
    const key = `*${r.name}`;
    if (out.some((o) => o.key === key)) return;
    out.push({
      key, label: key, dims: 0, root: r.name, resource: r, deref: true,
      pick: { variableId: r.id, accessType: 'scalar', accessPath: '', memberName: '', fullPath: key, dataType: pointee, dims: 0 },
    });
  });
  return out;
}
